import { z } from 'zod';

/**
 * The platform plane: authorization for the service provider, not the client.
 *
 * Everything in `permissions.ts` describes what somebody may do *inside* a
 * deployment -- read a project, retry a job, approve a budget. That plane
 * belongs to the client, and its administrator holds all of it. This one
 * describes what may be done *to* a deployment: which capabilities it has,
 * what it is called, what it looks like, whether it is open for business.
 * That belongs to whoever operates the service.
 *
 * The two are separate enums rather than one list with a naming convention,
 * and that is the whole security argument:
 *
 *   - `createRoleSchema` and `updateRoleSchema` validate against
 *     `permissionSchema`, so a platform permission is not a value a tenant
 *     role can even name. The escalation is refused by the parser, before any
 *     guard runs.
 *   - `canGrantPermissions` compares like with like. A client administrator
 *     holding every `Permission` still holds no `PlatformPermission`, so
 *     there is no set of tenant grants that adds up to platform access.
 *
 * A single flag on the user account would have been less code and worse: it
 * would make "provider" an all-or-nothing property, when the useful shape is a
 * support engineer who may read a deployment's configuration without being
 * able to re-theme it or switch its modules off.
 */
export const PLATFORM_PERMISSIONS = [
  /** See the provider console and the deployment record. */
  'platform.deployment.read',
  /** Deployment identity and lifecycle: client name, locale, stage. */
  'platform.deployment.manage',
  /** Design tokens: brand, colors, typography, chart palette. */
  'platform.branding.manage',
  /** Which modules and dashboard components this deployment has. */
  'platform.modules.manage',
  /**
   * The email-domain and Entra-tenant allowlists that decide who may sign in
   * or auto-register at all (access-control.ts). Kept apart from
   * `platform.admins.manage`: this only ever grants a *tenant* role or admits
   * a tenant to attempt sign-in, never the platform role itself.
   */
  'platform.access_control.manage',
  /**
   * The background job queue: what is pending, what is running, and what
   * dead-lettered.
   *
   * A provider permission rather than a tenant one, because the queue is a
   * property of the deployment rather than of the project. A client
   * administrator cannot act on `asset.prune_orphaned` failing four times; the
   * person who runs the installation can. On a self-hosted deployment those are
   * the same person, who holds the platform role and sees it in the console --
   * so nothing is hidden, it simply stops competing with the project for a
   * place in the navigation.
   *
   * Reading and retrying together, unlike the tenant `job.read`/`job.retry`
   * pair this replaces. In the provider plane the person who can see the queue
   * is the person who fixes it, and a split that never separates two people is
   * ceremony.
   */
  'platform.operations.manage',
  /**
   * Grant or revoke the platform role on a user account. The single most
   * sensitive permission in the system -- holding it is equivalent to being
   * able to make anyone, including yourself, fully omnipotent -- which is why
   * it is its own permission rather than folded into
   * `platform.access_control.manage` or `platform.deployment.manage`.
   */
  'platform.admins.manage',
] as const;

export type PlatformPermission = (typeof PLATFORM_PERMISSIONS)[number];
export const platformPermissionSchema = z.enum(PLATFORM_PERMISSIONS);

export function isPlatformPermission(value: string): value is PlatformPermission {
  return (PLATFORM_PERMISSIONS as readonly string[]).includes(value);
}

/**
 * Roles carry a plane, so a role row can never be read as belonging to the
 * other one.
 *
 * The tenant users and roles API filters on this rather than on the role's key
 * or its permission list. Filtering on data that the client can influence
 * would mean the isolation held only as long as nobody created a role that
 * looked like a platform role; filtering on a column the tenant API never
 * writes means it holds by construction.
 */
export const ROLE_SCOPES = ['tenant', 'platform'] as const;
export const roleScopeSchema = z.enum(ROLE_SCOPES);
export type RoleScope = (typeof ROLE_SCOPES)[number];

/**
 * The seeded provider role.
 *
 * It holds every tenant permission as well as the platform ones, because
 * standing up a deployment *is* tenant work: create the project, invite the
 * client's first administrator, connect the first integration. Splitting that
 * into a second account the provider would then have to hand-grant is
 * ceremony, not a control -- the account that can switch modules on can grant
 * itself anything anyway.
 *
 * `tenantPermissions` is deliberately not spelled out as a list here: it is
 * whatever the catalog says, so a new capability is provider-operable the day
 * it is added rather than the day somebody remembers this file.
 */
export const PLATFORM_ROLE = {
  key: 'system-administrator',
  nameKey: 'roles.systemAdministrator',
  scope: 'platform',
  permissions: [...PLATFORM_PERMISSIONS],
} as const;
